import { z } from "zod";
import {
  conditionStatusKey,
  type ConditionStatus,
  type MedConditionValue,
} from "./comorbidity";
import {
  EARLIER_LAB_RESULT_VALUES,
  earlierLabAnswers,
  type EarlierLabAnswers,
  type EarlierLabResult,
} from "./earlier-labs";

// TES-1116 (TES-1112 points 7–8) — F-02's conditional low-BMI section
// (internal key F02_LOW_BMI_ASSESSMENT) and the doctor's view of it: WHtR and
// Kirill's possible indicators.
//
// Sources, verbatim where quoted:
//   - Emelie 2026-10-05, TES-1112 points 7–8;
//   - Drive "A-02 Doctor Admin Surface", block "ÖVERORDNAT KANONISKT BESLUT —
//     LÄKARADMIN FÖR BMI 25,0–26,9 OCH OFF-LABEL-BEDÖMNING" (the indicator
//     list and its wording, which the labels below quote);
//   - Ö-337 (TES-475, 2026-10-05): Kirill's lab limits, "Use the operators
//     exactly as given".
//   - A-02 "KANONISK MEDICINSK FÖRTYDLIGANDE — BMI 25,0–26,9: HbA1c och
//     diagnosstatus" (Kirill via Emelie, TES-1150, 2026-10-07): Ö-346b
//     (HbA1c 39–41 only with a category 1–2 factor) and Ö-346c (only a
//     current PMOS/hypertension/fatty liver counts).
//
// "Systemet får identifiera, beräkna och presentera möjliga indikatorer men
//  får aldrig automatiskt godkänna off-label-behandling."
// Nothing here approves, blocks or flags: the output is a list the doctor
// reads. It is shared so the API and the admin agree by construction.

/** The section id in the intake template: Emelie's F02_LOW_BMI_ASSESSMENT. */
export const LOW_BMI_SECTION_ID = "f02_low_bmi_assessment";

/** F-02 answer keys of the section. Stable: a copy change never moves them. */
export const LOW_BMI_FIELDS = {
  height: "lowbmi_height",
  waist: "lowbmi_waist",
  waistMeasuredOn: "lowbmi_waist_measured_on",
  highestWeight: "lowbmi_highest_weight",
  highestWeightYear: "lowbmi_highest_weight_year",
  labs: "lowbmi_labs",
  hba1c: "lowbmi_lab_hba1c",
  triglycerides: "lowbmi_lab_triglycerides",
  hdl: "lowbmi_lab_hdl",
  ldl: "lowbmi_lab_ldl",
  labSampledOn: "lowbmi_lab_sampled_on",
  labProvider: "lowbmi_lab_provider",
  bia: "lowbmi_bia",
  biaBodyFat: "lowbmi_bia_body_fat",
  biaVisceralFat: "lowbmi_bia_visceral_fat",
  biaMeasuredOn: "lowbmi_bia_measured_on",
  biaSource: "lowbmi_bia_source",
  heredity: "lowbmi_heredity",
  loadPain: "lowbmi_load_pain",
  foodNoiseFrequency: "lowbmi_food_noise_frequency",
  foodNoiseImpact: "lowbmi_food_noise_impact",
  foodNoiseDespiteRoutines: "lowbmi_food_noise_despite_routines",
  foodNoiseNote: "lowbmi_food_noise_note",
} as const;

// TES-1125 — the same three options as the general F-02 question that owns
// them from intake v14 (earlier-labs.ts).
export const LOW_BMI_LAB_STATUSES = EARLIER_LAB_RESULT_VALUES;
export const LOW_BMI_HEREDITY_VALUES = [
  "tidig_hjart_karl",
  "typ2_diabetes",
  "metabolt_syndrom",
  "arftlig_hyperlipidemi",
  "inget",
  "vet_inte",
] as const;
export const FOOD_NOISE_FREQUENCY_VALUES = [
  "sallan",
  "nagon_gang_i_veckan",
  "flera_ganger_i_veckan",
  "dagligen",
  "storre_delen_av_dagen",
] as const;
export const FOOD_NOISE_IMPACT_VALUES = [
  "inte_alls",
  "lite",
  "mattligt",
  "mycket",
  "valdigt_mycket",
] as const;

/** Ö-337, as given. Units: HbA1c mmol/mol, lipids mmol/L. */
export const LOW_BMI_LIMITS = {
  whtrMin: 0.5, // WHtR ≥ 0,50
  bodyFatAbove: 31, // kroppsfett > 31 %
  visceralFatAbove: 9, // visceralt fett > 9
  hba1c: { min: 42, max: 47 }, // 42–47 mmol/mol
  hba1cWithFactor: { min: 39, max: 41 }, // 39–41 with another factor
  triglyceridesMin: 1.7, // ≥ 1,7
  hdlBelow: 1.3, // < 1,3
  ldlAbove: 3.0, // > 3,0
  tgHdlAbove: 1.65, // > 1,65
  earlierBmiMin: 27, // tidigare BMI ≥ 27
} as const;

/** The F-02 conditions point 8 names: PMOS, hypertension, fatty liver. */
export const LOW_BMI_CONDITIONS: readonly MedConditionValue[] = [
  "pcos",
  "hogt_blodtryck",
  "fettlever",
];

export const LOW_BMI_INDICATOR_IDS = [
  "whtr",
  "bia",
  "hba1c_42_47",
  "hba1c_39_41_with_factor",
  "triglycerides",
  "hdl",
  "ldl",
  "tg_hdl",
  "conditions",
  "heredity",
  "load_pain",
  "psychosocial",
  "earlier_bmi_27",
] as const;
export type LowBmiIndicatorId = (typeof LOW_BMI_INDICATOR_IDS)[number];

/**
 * A-02's five numbered categories of possible indicators:
 *   1 central adiposity (WHtR, BIA);
 *   2 cardiometabolic comorbidity or risk (HbA1c, lipids, TG/HDL, PMOS/
 *     hypertension/fatty liver, heredity);
 *   3 load pain; 4 food noise / psychological burden; 5 relapse prevention
 *     (earlier BMI ≥ 27).
 * Ö-346b: HbA1c 39–41 counts only together with another category 1 or 2
 * factor — never with 3–5 alone.
 */
export const LOW_BMI_INDICATOR_CATEGORY: Record<
  LowBmiIndicatorId,
  1 | 2 | 3 | 4 | 5
> = {
  whtr: 1,
  bia: 1,
  hba1c_42_47: 2,
  hba1c_39_41_with_factor: 2,
  triglycerides: 2,
  hdl: 2,
  ldl: 2,
  tg_hdl: 2,
  conditions: 2,
  heredity: 2,
  load_pain: 3,
  psychosocial: 4,
  earlier_bmi_27: 5,
};

// Staff copy. Quoted from A-02's indicator list; the psychosocial line is
// Emelie's (TES-1112 point 7), verbatim.
export const LOW_BMI_INDICATOR_LABELS: Record<LowBmiIndicatorId, string> = {
  whtr: "Central adipositet: WHtR minst 0,50",
  bia: "Central adipositet: BIA med kroppsfett över 31 procent eller visceralt fett över 9",
  hba1c_42_47: "HbA1c 42–47 mmol/mol",
  hba1c_39_41_with_factor:
    "HbA1c 39–41 mmol/mol tillsammans med central adipositet eller annan kardiometabol riskfaktor",
  triglycerides: "Triglycerider minst 1,7 mmol/L",
  hdl: "HDL-kolesterol under 1,3 mmol/L",
  ldl: "LDL-kolesterol över 3,0 mmol/L",
  tg_hdl: "TG/HDL-kvot över 1,65",
  // Ö-346c: only a current diagnosis counts. "tidigare kallat" because a
  // bare "(tidigare PCOS)" next to "tidigare diagnos" reads as history.
  conditions:
    "Aktuell PMOS (tidigare kallat PCOS), hypertoni eller fettlever/NAFLD",
  heredity:
    "Förstagradssläkting med tidig kardiovaskulär sjukdom, typ 2-diabetes, metabolt syndrom eller ärftlig hyperlipidemi/hyperkolesterolemi",
  load_pain:
    "Belastningssmärta: viktrelaterad smärta i knän, höfter eller rygg som begränsar fysisk aktivitet",
  psychosocial: "Möjlig psykosocial indikator – behöver bedömas under mötet",
  earlier_bmi_27: "Tidigare BMI minst 27",
};

// --- DTO ----------------------------------------------------------------------

export const lowBmiIndicatorSchema = z.object({
  id: z.enum(LOW_BMI_INDICATOR_IDS),
  label: z.string(),
  /** The values behind it, e.g. "HbA1c 44 mmol/mol". */
  detail: z.string(),
});
export type LowBmiIndicator = z.infer<typeof lowBmiIndicatorSchema>;

/**
 * Ö-346c — a PMOS, hypertension or fatty liver diagnosis that does NOT count
 * as an indicator: "tidigare" is shown as history, "osaker" as something to
 * clarify. A selected condition without a status answer is shown as
 * "osaker" — it is not a confirmed current diagnosis either.
 */
export const lowBmiConditionNoteSchema = z.object({
  condition: z.enum(["pcos", "hogt_blodtryck", "fettlever"]),
  /** "PMOS", "hypertoni", "fettlever". */
  name: z.string(),
  status: z.enum(["tidigare", "osaker"]),
});
export type LowBmiConditionNote = z.infer<typeof lowBmiConditionNoteSchema>;

export const lowBmiMeasuresSchema = z.object({
  heightCm: z.number().nullable(),
  /** 'section' = the confirmed height in the section; 'intake' = med_height. */
  heightSource: z.enum(["section", "intake"]).nullable(),
  waistCm: z.number().nullable(),
  waistMeasuredOn: z.string().nullable(),
  /**
   * TES-1649 (Ö-1018) — where the waist came from: F-02's own question
   * (intake v13–v26), or the separate waist task after F-02 (intake v27 on,
   * the shared WAIST_CM series). Null without a waist.
   */
  waistSource: z.enum(["intake", "waist_task"]).nullable(),
  /**
   * waist / height, unrounded — the value compared (Ö-346d). Show it with
   * formatWhtr, never a plain two-decimal rounding.
   */
  whtr: z.number().nullable(),
  highestWeightKg: z.number().nullable(),
  highestWeightYear: z.number().nullable(),
  /** highest adult weight over the height, one decimal. */
  earlierBmi: z.number().nullable(),
  labStatus: z.enum(LOW_BMI_LAB_STATUSES).nullable(),
  hba1c: z.number().nullable(),
  triglycerides: z.number().nullable(),
  hdl: z.number().nullable(),
  ldl: z.number().nullable(),
  /** TG / HDL, unrounded — the value compared (Ö-346d); show it with formatTgHdl. */
  tgHdl: z.number().nullable(),
  labSampledOn: z.string().nullable(),
  labProvider: z.string().nullable(),
  bodyFatPct: z.number().nullable(),
  visceralFat: z.number().nullable(),
  biaMeasuredOn: z.string().nullable(),
  biaSource: z.string().nullable(),
  heredity: z.array(z.string()),
  loadPain: z.string().nullable(),
  foodNoiseFrequency: z.string().nullable(),
  foodNoiseImpact: z.string().nullable(),
  foodNoiseDespiteRoutines: z.string().nullable(),
  foodNoiseNote: z.string().nullable(),
});
export type LowBmiMeasures = z.infer<typeof lowBmiMeasuresSchema>;

/** GET employee-api/v1/patients/:id/low-bmi-assessment */
export const lowBmiAssessmentSchema = z.object({
  intake: z
    .object({
      submissionId: z.string(),
      templateVersion: z.number().int(),
      submittedAt: z.string(),
    })
    .nullable(),
  /** Null when the latest F-02 carries no answer from the section. */
  section: z
    .object({
      measures: lowBmiMeasuresSchema,
      indicators: z.array(lowBmiIndicatorSchema),
      /** Ö-346c: earlier or uncertain diagnoses — shown, never counted. */
      conditionNotes: z.array(lowBmiConditionNoteSchema),
    })
    .nullable(),
});
export type LowBmiAssessment = z.infer<typeof lowBmiAssessmentSchema>;

// --- evaluation ---------------------------------------------------------------

function num(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n =
    typeof raw === "number" ? raw : Number(String(raw).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function str(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

function round(n: number, decimals: number): number {
  const f = 10 ** decimals;
  // Same 1e-9 nudge as roundBmi: 0.495 must not round down to 0.49.
  return Math.round(n * f + 1e-9) / f;
}

/** "0,52" — Swedish decimal comma, no trailing zeros dropped by the caller. */
export function formatSvNumber(n: number, decimals?: number): string {
  const s = decimals === undefined ? String(n) : n.toFixed(decimals);
  return s.replace(".", ",");
}

// Ö-346d (Emelie 2026-10-06): the two ratios compare their UNROUNDED value
// — WHtR 0,4994 does not meet "≥ 0,50". The doctor is shown two decimals
// unless that rounding would contradict the rule (0,4994 → "0,50" next to
// "WHtR minst 0,50"); then as many more decimals as it takes to agree with
// it, so 0,4994 reads "0,499" and a TG/HDL of 1,6501 reads "1,6501".
function formatAgainstLimit(
  raw: number,
  meets: (v: number) => boolean,
): string {
  for (let d = 2; d <= 6; d++) {
    const shown = round(raw, d);
    if (meets(shown) === meets(raw)) return formatSvNumber(shown, d);
  }
  return formatSvNumber(raw);
}

export const whtrMeets = (v: number): boolean => v >= LOW_BMI_LIMITS.whtrMin;
export const tgHdlMeets = (v: number): boolean => v > LOW_BMI_LIMITS.tgHdlAbove;

/** "0,51"; "0,499" when two decimals would read as meeting ≥ 0,50. */
export function formatWhtr(raw: number): string {
  return formatAgainstLimit(raw, whtrMeets);
}

/** "1,50"; "1,6501" when two decimals would read as not above 1,65. */
export function formatTgHdl(raw: number): string {
  return formatAgainstLimit(raw, tgHdlMeets);
}

/**
 * TES-1649 (Ö-1018) — from intake v27 the waist is not asked in F-02 but in
 * its own task after it. The doctor's measures take that measurement when
 * the declaration has none, so WHtR is still computed from the same inputs.
 */
export function withWaistTaskMeasure(
  measures: LowBmiMeasures,
  waist: { waistCm: number; measuredOn: string } | null,
): LowBmiMeasures {
  if (measures.waistCm !== null || waist === null) return measures;
  return {
    ...measures,
    waistCm: waist.waistCm,
    waistMeasuredOn: waist.measuredOn,
    waistSource: "waist_task",
    whtr:
      measures.heightCm !== null ? waist.waistCm / measures.heightCm : null,
  };
}

/** Does this F-02 answer set carry the section? */
export function hasLowBmiSection(answers: Record<string, unknown>): boolean {
  return Object.values(LOW_BMI_FIELDS).some(
    (k) => answers[k] !== undefined && answers[k] !== null,
  );
}

// The lab status the doctor's low-BMI panel shows: her answer to "Har du
// provsvar …?" when she gave one; otherwise what follows from the question
// before it in v14 — "Nej" to ever having given blood samples is "Jag har
// inga provsvar", "Osäker" is "Jag vet inte". "Ja" with the follow-up left
// unanswered stays unknown.
function lowBmiLabStatus(labs: EarlierLabAnswers): EarlierLabResult | null {
  if (labs.results) return labs.results;
  if (labs.stated === "nej") return "inga_provsvar";
  if (labs.stated === "osaker") return "vet_inte";
  return null;
}

export function lowBmiMeasures(
  answers: Record<string, unknown>,
): LowBmiMeasures {
  const F = LOW_BMI_FIELDS;
  const sectionHeight = num(answers[F.height]);
  const intakeHeight = num(answers["med_height"]);
  const heightCm = sectionHeight ?? intakeHeight;
  const waistCm = num(answers[F.waist]);
  const highestWeightKg = num(answers[F.highestWeight]);
  const hdl = num(answers[F.hdl]);
  const triglycerides = num(answers[F.triglycerides]);
  // TES-1125 — from intake v14 the general F-02 block owns whether she has
  // results, the date and the provider; v13 asked them in this section.
  const labs = earlierLabAnswers(answers);
  const heredityRaw = answers[F.heredity];
  return {
    heightCm,
    heightSource:
      sectionHeight !== null
        ? "section"
        : intakeHeight !== null
          ? "intake"
          : null,
    waistCm,
    waistMeasuredOn: str(answers[F.waistMeasuredOn]),
    waistSource: waistCm !== null ? "intake" : null,
    whtr: waistCm !== null && heightCm !== null ? waistCm / heightCm : null,
    highestWeightKg,
    highestWeightYear: num(answers[F.highestWeightYear]),
    earlierBmi:
      highestWeightKg !== null && heightCm !== null
        ? round(highestWeightKg / (heightCm / 100) ** 2, 1)
        : null,
    labStatus: lowBmiLabStatus(labs),
    hba1c: num(answers[F.hba1c]),
    triglycerides,
    hdl,
    ldl: num(answers[F.ldl]),
    tgHdl: triglycerides !== null && hdl !== null ? triglycerides / hdl : null,
    labSampledOn: labs.sampledOn,
    labProvider: labs.provider,
    bodyFatPct: num(answers[F.biaBodyFat]),
    visceralFat: num(answers[F.biaVisceralFat]),
    biaMeasuredOn: str(answers[F.biaMeasuredOn]),
    biaSource: str(answers[F.biaSource]),
    heredity: Array.isArray(heredityRaw)
      ? heredityRaw.filter((v): v is string => typeof v === "string")
      : [],
    loadPain: str(answers[F.loadPain]),
    foodNoiseFrequency: str(answers[F.foodNoiseFrequency]),
    foodNoiseImpact: str(answers[F.foodNoiseImpact]),
    foodNoiseDespiteRoutines: str(answers[F.foodNoiseDespiteRoutines]),
    foodNoiseNote: str(answers[F.foodNoiseNote]),
  };
}

// The psychosocial line's trigger (Ö-346a, Emelie 2026-10-06): thoughts
// about food or weight at least daily, OR a large impact ("Mycket" or more)
// on quality of life, wellbeing or focus — AND an explicit "Ja" or "Osäker"
// to persisting despite established routines (A-02: "trots etablerade goda
// kost- och träningsrutiner"). An unanswered persistence question does not
// fire it. The line is a possible indicator the doctor assesses in the
// meeting, never a risk flag.
const FREQUENT: readonly string[] = ["dagligen", "storre_delen_av_dagen"];
const HIGH_IMPACT: readonly string[] = ["mycket", "valdigt_mycket"];
const PERSISTS: readonly string[] = ["ja", "osaker"];

export function psychosocialIndicated(m: LowBmiMeasures): boolean {
  const frequent =
    m.foodNoiseFrequency !== null && FREQUENT.includes(m.foodNoiseFrequency);
  const impact =
    m.foodNoiseImpact !== null && HIGH_IMPACT.includes(m.foodNoiseImpact);
  return (
    (frequent || impact) && PERSISTS.includes(m.foodNoiseDespiteRoutines ?? "")
  );
}

const HEREDITY_POSITIVE: readonly string[] = [
  "tidig_hjart_karl",
  "typ2_diabetes",
  "metabolt_syndrom",
  "arftlig_hyperlipidemi",
];

const CONDITION_NAMES: Record<LowBmiConditionNote["condition"], string> = {
  pcos: "PMOS",
  hogt_blodtryck: "hypertoni",
  fettlever: "fettlever",
};

/**
 * Each of PMOS, hypertension and fatty liver she selected in F-02, with the
 * status she gave it (Ö-346c). A missing or unknown status is "osaker".
 */
function lowBmiConditionStatuses(
  answers: Record<string, unknown>,
): { condition: LowBmiConditionNote["condition"]; status: ConditionStatus }[] {
  const selected = Array.isArray(answers["med_conditions"])
    ? (answers["med_conditions"] as unknown[])
    : [];
  return (LOW_BMI_CONDITIONS as readonly LowBmiConditionNote["condition"][])
    .filter((c) => selected.includes(c))
    .map((condition) => {
      const raw = answers[conditionStatusKey(condition)];
      const status: ConditionStatus =
        raw === "aktuellt" || raw === "tidigare" ? raw : "osaker";
      return { condition, status };
    });
}

/** Ö-346c: the selected conditions that are NOT current, for display only. */
export function lowBmiConditionNotes(
  answers: Record<string, unknown>,
): LowBmiConditionNote[] {
  return lowBmiConditionStatuses(answers).flatMap(({ condition, status }) =>
    status === "aktuellt"
      ? []
      : [{ condition, name: CONDITION_NAMES[condition], status }],
  );
}

/**
 * Kirill's possible indicators that the answers meet, in A-02's order.
 * Only met indicators are returned; the doctor reads the values in
 * `measures`. Every comparison uses the operator Ö-337/A-02 gives; the two
 * ratios compare their unrounded value (Ö-346d).
 */
export function evaluateLowBmiIndicators(
  answers: Record<string, unknown>,
  measures: LowBmiMeasures = lowBmiMeasures(answers),
): LowBmiIndicator[] {
  const m = measures;
  const L = LOW_BMI_LIMITS;
  const out: LowBmiIndicator[] = [];
  const add = (id: LowBmiIndicatorId, detail: string) =>
    out.push({ id, label: LOW_BMI_INDICATOR_LABELS[id], detail });

  if (m.whtr !== null && whtrMeets(m.whtr)) {
    add("whtr", `WHtR ${formatWhtr(m.whtr)}`);
  }
  const fat = m.bodyFatPct !== null && m.bodyFatPct > L.bodyFatAbove;
  const visceral = m.visceralFat !== null && m.visceralFat > L.visceralFatAbove;
  if (fat || visceral) {
    add(
      "bia",
      [
        m.bodyFatPct !== null
          ? `kroppsfett ${formatSvNumber(m.bodyFatPct)} %`
          : null,
        m.visceralFat !== null
          ? `visceralt fett ${formatSvNumber(m.visceralFat)}`
          : null,
      ]
        .filter(Boolean)
        .join(", "),
    );
  }
  // HbA1c 39–41 needs another factor, so it is decided after the rest.
  const hba1cDetail =
    m.hba1c !== null ? `HbA1c ${formatSvNumber(m.hba1c)} mmol/mol` : "";
  if (m.hba1c !== null && m.hba1c >= L.hba1c.min && m.hba1c <= L.hba1c.max) {
    add("hba1c_42_47", hba1cDetail);
  }
  if (m.triglycerides !== null && m.triglycerides >= L.triglyceridesMin) {
    add(
      "triglycerides",
      `Triglycerider ${formatSvNumber(m.triglycerides)} mmol/L`,
    );
  }
  if (m.hdl !== null && m.hdl < L.hdlBelow) {
    add("hdl", `HDL ${formatSvNumber(m.hdl)} mmol/L`);
  }
  if (m.ldl !== null && m.ldl > L.ldlAbove) {
    add("ldl", `LDL ${formatSvNumber(m.ldl)} mmol/L`);
  }
  if (m.tgHdl !== null && tgHdlMeets(m.tgHdl)) {
    add("tg_hdl", `TG/HDL ${formatTgHdl(m.tgHdl)}`);
  }

  // Ö-346c: only a current diagnosis is an indicator; earlier and uncertain
  // ones are returned by lowBmiConditionNotes instead.
  const current = lowBmiConditionStatuses(answers).filter(
    (c) => c.status === "aktuellt",
  );
  if (current.length > 0) {
    add(
      "conditions",
      current
        .map((c) => `${CONDITION_NAMES[c.condition]} (aktuell)`)
        .join(", "),
    );
  }

  if (m.heredity.some((v) => HEREDITY_POSITIVE.includes(v))) {
    add("heredity", "Enligt patientens svar i F-02");
  }
  if (m.loadPain === "ja") {
    add("load_pain", "Patienten svarade ja i F-02");
  }
  if (psychosocialIndicated(m)) {
    add("psychosocial", "Enligt patientens svar om food noise i F-02");
  }
  if (m.earlierBmi !== null && m.earlierBmi >= L.earlierBmiMin) {
    add(
      "earlier_bmi_27",
      `BMI ${formatSvNumber(m.earlierBmi, 1)} vid högsta vuxenvikt ${formatSvNumber(m.highestWeightKg!)} kg${m.highestWeightYear !== null ? ` (${m.highestWeightYear})` : ""}`,
    );
  }

  // Ö-346b: HbA1c 39–41 is only a reinforcing biomarker. It counts when
  // another category 1 or 2 factor is met (42–47 cannot be met at the same
  // time); load pain, food noise and earlier BMI (categories 3–5) never make
  // the combination. Placed after the central-adiposity lines, where A-02
  // lists HbA1c.
  if (
    m.hba1c !== null &&
    m.hba1c >= L.hba1cWithFactor.min &&
    m.hba1c <= L.hba1cWithFactor.max &&
    out.some((i) => LOW_BMI_INDICATOR_CATEGORY[i.id] <= 2)
  ) {
    const at = out.filter((i) => i.id === "whtr" || i.id === "bia").length;
    out.splice(at, 0, {
      id: "hba1c_39_41_with_factor",
      label: LOW_BMI_INDICATOR_LABELS.hba1c_39_41_with_factor,
      detail: hba1cDetail,
    });
  }
  return out;
}
